import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, TextField, Typography, Grid, Autocomplete } from "@mui/material";
import { debounce } from "@mui/material/utils";
import { useTranslation } from "react-i18next";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { useAuth } from "../../context/authContext";
import useActorAllocationScope from "../../hooks/useActorAllocationScope";
import ManageSiteMachines from "../../components/ManageSiteMachines/ManageSiteMachines";

const AdminDocumentationEquipment = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    scopeLoading,
    actorSiteIds,
    actorCustomerIds,
    actorHasAllSiteVisibility,
  } = useActorAllocationScope();

  const [customersOptions, setCustomersOptions] = useState([]);
  const [sitesOptions, setSitesOptions] = useState([]);
  const [customerValue, setCustomerValue] = useState(null);
  const [locationValue, setLocationValue] = useState(null);
  const [customerInput, setCustomerInput] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [loadingFilters, setLoadingFilters] = useState(false);

  const ALL_CUSTOMERS_OPTION = useMemo(
    () => ({
      value: t("ADMIN_FILTERS.ALL_CUSTOMERS"),
      label: t("ADMIN_FILTERS.ALL_CUSTOMERS"),
    }),
    [t]
  );

  const ALL_LOCATIONS_OPTION = useMemo(
    () => ({
      value: t("ADMIN_FILTERS.ALL_LOCATIONS"),
      label: t("ADMIN_FILTERS.ALL_LOCATIONS"),
    }),
    [t]
  );

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await getCustomersCusSites();
      if (!response?.status) {
        setCustomersOptions([]);
        return;
      }

      const allCustomers = (response.data || []).filter(
        (customer) => customer.customer_name && customer.customer_name !== "EMPTY"
      );
      const filteredCustomers = actorHasAllSiteVisibility
        ? allCustomers
        : allCustomers.filter((customer) => actorCustomerIds.includes(Number(customer.cus_id)));

      setCustomersOptions(
        filteredCustomers.map((customer) => ({
          value: customer.cus_id,
          label: `${customer.customer_name} - ${customer.customer_number}`,
          ...customer,
        }))
      );
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomersOptions([]);
    }
  }, [actorCustomerIds, actorHasAllSiteVisibility]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await getAllCustomerSites();
      if (!response?.status) {
        setSitesOptions([]);
        return;
      }

      const allSites = (response.data || []).filter(
        (site) => site.customer_site_name && site.customer_site_name !== "EMPTY"
      );
      const filteredSites = actorHasAllSiteVisibility
        ? allSites
        : allSites.filter((site) => actorSiteIds.includes(Number(site.site_id)));

      setSitesOptions(
        filteredSites.map((site) => ({
          value: site.site_id,
          label: `${site.customer_site_name} - ${site.customer_site_number}`,
          ...site,
        }))
      );
    } catch (error) {
      console.error("Error fetching sites:", error);
      setSitesOptions([]);
    }
  }, [actorHasAllSiteVisibility, actorSiteIds]);

  useEffect(() => {
    if (!user || scopeLoading) return;

    let mounted = true;
    const init = async () => {
      setLoadingFilters(true);
      await Promise.all([fetchCustomers(), fetchSites()]);
      if (mounted) setLoadingFilters(false);
    };

    init();
    return () => {
      mounted = false;
    };
  }, [fetchCustomers, fetchSites, scopeLoading, user]);

  const debouncedCustomerInput = useCallback(
    debounce((value) => {
      setCustomerInput(value);
    }, 300),
    []
  );

  const debouncedLocationInput = useCallback(
    debounce((value) => {
      setLocationInput(value);
    }, 300),
    []
  );

  const filteredCustomersOptions = useMemo(() => {
    if (!customerInput.trim()) return customersOptions;
    return customersOptions.filter((option) =>
      option.label.toLowerCase().includes(customerInput.toLowerCase())
    );
  }, [customerInput, customersOptions]);

  const filteredSitesOptions = useMemo(() => {
    let filtered = sitesOptions;

    if (customerValue?.value) {
      filtered = filtered.filter((option) => String(option?.cus_id) === String(customerValue.value));
    }

    if (!locationInput.trim()) return filtered;
    return filtered.filter((option) =>
      option.label.toLowerCase().includes(locationInput.toLowerCase())
    );
  }, [customerValue?.value, locationInput, sitesOptions]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          mb: 2,
          mt: 1,
          display: "flex",
          flexDirection: "column",
          rowGap: "5px",
          background: "#eee",
          p: 2,
          borderRadius: "10px",
        }}
      >
        <Typography fontWeight={600} mb={0.5}>
          {t("ADMIN_FILTERS.TITLE")}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t("ADMIN_FILTERS.DESCRIPTIONEQUIP")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={[ALL_CUSTOMERS_OPTION, ...filteredCustomersOptions]}
              value={customerValue}
              onChange={(_, newValue) => {
                const isAllCustomers =
                  !newValue ||
                  newValue.value === null ||
                  newValue.value === ALL_CUSTOMERS_OPTION.value;

                if (isAllCustomers) {
                  setCustomerValue(null);
                  setLocationValue(null);
                  return;
                }

                setCustomerValue(newValue);

                if (locationValue?.value && String(locationValue.cus_id) !== String(newValue.value)) {
                  setLocationValue(null);
                }
              }}
              onInputChange={(_, newInputValue) => {
                debouncedCustomerInput(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) => option?.label || ""}
              loading={loadingFilters}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("ADMIN_FILTERS.CUSTOMER")}
                  placeholder={t("ADMIN_FILTERS.SEARCH_CUSTOMERS")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={[ALL_LOCATIONS_OPTION, ...filteredSitesOptions]}
              value={locationValue}
              onChange={(_, newValue) => {
                const isAllLocations =
                  !newValue ||
                  newValue.value === null ||
                  newValue.value === ALL_LOCATIONS_OPTION.value;
                if (isAllLocations) {
                  setLocationValue(null);
                  return;
                }

                setLocationValue(newValue);

                if (!customerValue?.value) {
                  const relatedCustomer = customersOptions.find(
                    (customer) => String(customer.value) === String(newValue.cus_id)
                  );
                  if (relatedCustomer) {
                    setCustomerValue(relatedCustomer);
                  }
                }
              }}
              onInputChange={(_, newInputValue) => {
                debouncedLocationInput(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) => option?.label || ""}
              loading={loadingFilters}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("ADMIN_FILTERS.LOCATION")}
                  placeholder={t("ADMIN_FILTERS.SEARCH_LOCATIONS")}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ overflow: "visible", width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            overflow: "visible",
            position: "relative"
          }}
        >
          <ManageSiteMachines
            hideFilters={true}
            selectedCustomer={customerValue}
            selectedSiteData={locationValue}
            showDocumentFetchColumn={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDocumentationEquipment;
